import React, {useCallback, useEffect, useState} from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import {
  cancelDependentAppointment,
  fetchDependentCancellationPolicy,
  fetchDependentRescheduleOptions,
  messageFor,
  rescheduleDependentAppointment,
} from "../../api/dependents";
import {APP_LOCALE, formatDateTime} from "./format";
import {formatPhone, phoneHref} from "../../utils/phone";
import {
  closedCopyFor,
  dayOn,
  mostInformativeReason,
  noticeSentence,
  refusalFrom,
  showsTwoClocks,
  timeOn,
  viewerTimeZone,
  zoneLabel,
} from "../../services/schedule/rescheduleOptions";
import {deliveryModeOfAppointment} from "../../services/schedule/deliveryMode";

// Cancelling and rescheduling a dependent's appointment.
//
// Both dialogs talk to the appointment's OWN canonical endpoints — the same
// ones the guardian's personal appointments use — because the appointment names
// whose it is and the server derives the subject from the record. There is no
// dependent-scoped cancel URL to keep in step, no second cancellation window,
// and no second fee rule.
//
// The clinic's policy is asked BEFORE anything is offered, and its refusal is
// shown in the clinic's own words. A portal that decides for itself when a
// cancellation is too late will eventually disagree with the clinic that set
// the number.
//
// No `window.confirm` anywhere: a native dialog cannot say what the clinic's
// notice period is, cannot be read by the same assistive tech as the page, and
// blocks the tab it opens on.

const DAY_MS = 24 * 60 * 60 * 1000;
// How far "Show later dates" reaches each time. The FIRST window is the
// server's own default; how far a patient may book is the clinic's rule, and
// the server applies it to every answer.
const MORE_DAYS = 28;

/** The clinic's answer to "can this be changed online at all?", loaded once. */
function useModificationPolicy(state, appointmentId, isOpen) {
  const [policy, setPolicy] = useState(null);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !appointmentId) return;
    let cancelled = false;
    setStatus("loading");
    fetchDependentCancellationPolicy(state, appointmentId)
      .then((data) => {
        if (cancelled) return;
        setPolicy(data);
        setStatus("ready");
      })
      .catch((err) => {
        if (cancelled) return;
        setError(messageFor(err, "We could not check your clinic's policy."));
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [state, appointmentId, isOpen]);

  return {policy, status, error};
}

function PolicyBlock({policy, error}) {
  const message = error || policy?.blockedMessage;
  if (!message) return null;
  return (
    <Alert status="warning" borderRadius="10px" fontSize="sm" alignItems="flex-start">
      <AlertIcon />
      <Box>
        <Text>{message}</Text>
        {policy?.clinicPhone && (
          <Link href={phoneHref(policy.clinicPhone)} display="inline-block" mt={1} fontWeight="600" color="blue.600">
            {formatPhone(policy.clinicPhone)}
          </Link>
        )}
      </Box>
    </Alert>
  );
}

export function CancelAppointmentDialog({
  state,
  appointment,
  displayName,
  timeZone,
  isOpen,
  onClose,
  onDone,
}) {
  const {policy, status, error} = useModificationPolicy(state, appointment?.appointmentId, isOpen);
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  const canProceed = status === "ready" && policy?.canModify === true;

  const submit = async () => {
    setSubmitting(true);
    try {
      await cancelDependentAppointment(state, appointment.appointmentId, reason.trim() || undefined);
      toast({
        status: "success",
        title: "Appointment cancelled",
        description: `${displayName}'s appointment has been cancelled and the clinic has been told.`,
        duration: 6000,
        isClosable: true,
      });
      onDone?.();
      onClose();
    } catch (err) {
      toast({
        status: "error",
        title: "It could not be cancelled",
        description: messageFor(err, "Please try again, or contact your clinic."),
        duration: 8000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader color="#1a1f36">Cancel this appointment</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={4}>
            <Box border="1px solid" borderColor="gray.200" borderRadius="10px" p={4}>
              <Text fontWeight="600" color="#1a1f36">
                {appointment?.serviceName || "Appointment"}
              </Text>
              <Text fontSize="sm" color="gray.600">
                {formatDateTime(appointment?.startsAt, timeZone)}
                {appointment?.clinicianName ? ` · ${appointment.clinicianName}` : ""}
              </Text>
              <Text fontSize="sm" color="gray.600" mt={1}>
                For {displayName}
              </Text>
            </Box>

            {status === "loading" && (
              <Flex align="center" gap={2}>
                <Spinner size="sm" color="orange.400" />
                <Text fontSize="sm" color="gray.600">
                  Checking your clinic's cancellation policy…
                </Text>
              </Flex>
            )}

            <PolicyBlock policy={policy} error={error} />

            {canProceed && (
              <Box>
                <Text fontSize="sm" color="gray.700" mb={2}>
                  Would you like to tell the clinic why? (optional)
                </Text>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="No longer needed, unwell, schedule clash…"
                  rows={3}
                  fontSize="sm"
                />
              </Box>
            )}
          </Stack>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={onClose} isDisabled={submitting}>
            Keep it
          </Button>
          <Button
            colorScheme="red"
            onClick={submit}
            isLoading={submitting}
            isDisabled={!canProceed}
          >
            Cancel appointment
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export function RescheduleAppointmentDialog({
  state,
  appointment,
  displayName,
  timeZone: clinicTimeZone,
  isOpen,
  onClose,
  onDone,
}) {
  const {policy, status: policyStatus, error: policyError} = useModificationPolicy(
    state,
    appointment?.appointmentId,
    isOpen
  );
  const [days, setDays] = useState(null);
  // Starts as the zone the list was drawn in, and is replaced by the one the
  // availability answer names. Both are the clinic's; they can differ only if
  // the appointment sits at a site with its own zone, and then the second is
  // the right one — jurisdiction and clock both follow the site.
  const [timeZone, setTimeZone] = useState(clinicTimeZone || null);
  const [optionsStatus, setOptionsStatus] = useState("idle");
  const [optionsError, setOptionsError] = useState(null);
  // What the server answered last: where its window ended, and the notice the
  // clinic asks for (already applied to the times it returned).
  const [windowEnd, setWindowEnd] = useState(null);
  const [notice, setNotice] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [chosen, setChosen] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  const canProceed = policyStatus === "ready" && policy?.canModify === true;
  // Video only: the visit with a meeting link (§2.7).
  const isVirtual = deliveryModeOfAppointment(appointment) === "videoconference";
  const viewerZone = viewerTimeZone();

  // The appointment to leave out of the search is the one being moved, and the
  // server derives it from the path — an appointment must not block its own
  // reschedule, and a client naming the exclusion is a client the server would
  // have to trust about whose appointment it is.
  const ask = useCallback(
    (range) => fetchDependentRescheduleOptions(state, appointment.appointmentId, range),
    [state, appointment?.appointmentId]
  );

  const remember = useCallback((data) => {
    // The clinic's own time zone, from the answer. Every time on this screen
    // is drawn in it — see the note on `timeLabel`.
    if (data?.meta?.timeZone) setTimeZone(data.meta.timeZone);
    setWindowEnd(Number(data?.meta?.to) || null);
    setNotice(data?.meta?.notice || null);
  }, []);

  const loadOptions = useCallback(async () => {
    setOptionsStatus("loading");
    setOptionsError(null);
    try {
      // No range: the first window is the server's.
      const data = await ask({});
      setDays(data?.days || {});
      remember(data);
      setOptionsStatus("ready");
    } catch (err) {
      setOptionsError(refusalFrom(err) || {message: messageFor(err, "We could not load available times."), clinicPhone: null});
      setOptionsStatus("error");
    }
  }, [ask, remember]);

  const loadMore = async () => {
    if (!windowEnd) return;
    setLoadingMore(true);
    try {
      const data = await ask({from: windowEnd, to: windowEnd + MORE_DAYS * DAY_MS});
      setDays((previous) => ({...(previous || {}), ...(data?.days || {})}));
      remember(data);
    } catch (err) {
      toast({
        status: "error",
        title: "No more dates could be loaded",
        description: refusalFrom(err)?.message || messageFor(err, "Please try again."),
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (canProceed && optionsStatus === "idle") loadOptions();
  }, [canProceed, optionsStatus, loadOptions]);

  useEffect(() => {
    if (!isOpen) {
      setOptionsStatus("idle");
      setChosen(null);
      setDays(null);
      setWindowEnd(null);
      setNotice(null);
      setTimeZone(clinicTimeZone || null);
    }
  }, [isOpen, clinicTimeZone]);

  const submit = async () => {
    setSubmitting(true);
    try {
      await rescheduleDependentAppointment(state, appointment.appointmentId, chosen);
      toast({
        status: "success",
        title: "Appointment moved",
        description: `${displayName}'s appointment is now ${dayLabel(chosen.dayKey)} at ${timeLabel(chosen.start, timeZone)}.`,
        duration: 6000,
        isClosable: true,
      });
      onDone?.();
      onClose();
    } catch (err) {
      toast({
        status: "error",
        title: "It could not be moved",
        description: messageFor(err, "That time may have just been taken. Please pick another."),
        duration: 8000,
        isClosable: true,
      });
      // The slot list is now stale by definition — somebody else took it.
      loadOptions();
      setChosen(null);
    } finally {
      setSubmitting(false);
    }
  };

  const dayEntries = Object.entries(days || {})
    .filter(([, day]) => (day?.slots || []).length > 0)
    .sort(([a], [b]) => (a < b ? -1 : 1));
  // When nothing is open, say why — the reason a guardian can act on first.
  const emptyCopy = dayEntries.length === 0 ? closedCopyFor(mostInformativeReason(days)) : null;
  const noticeLine = noticeSentence(notice);
  const twoClocks = showsTwoClocks({
    isVirtual,
    clinicTimeZone: timeZone,
    viewerZone,
    at: dayEntries[0]?.[1]?.slots?.[0]?.start || Date.now(),
  });
  const showLater = canProceed && optionsStatus === "ready" && windowEnd ? (
    <Button size="sm" variant="ghost" alignSelf="flex-start" onClick={loadMore} isLoading={loadingMore}>
      Show later dates
    </Button>
  ) : null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="85vh">
        <ModalHeader color="#1a1f36">Move this appointment</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={4}>
            <Box border="1px solid" borderColor="gray.200" borderRadius="10px" p={4}>
              <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.04em" color="gray.500">
                Currently
              </Text>
              <Text fontWeight="600" color="#1a1f36">
                {formatDateTime(appointment?.startsAt, timeZone)}
              </Text>
              <Text fontSize="sm" color="gray.600">
                {appointment?.serviceName || "Appointment"}
                {appointment?.clinicianName ? ` · ${appointment.clinicianName}` : ""} · for{" "}
                {displayName}
              </Text>
            </Box>

            {policyStatus === "loading" && (
              <Flex align="center" gap={2}>
                <Spinner size="sm" color="orange.400" />
                <Text fontSize="sm" color="gray.600">
                  Checking your clinic's policy…
                </Text>
              </Flex>
            )}

            <PolicyBlock policy={policy} error={policyError} />

            {canProceed && optionsStatus === "loading" && (
              <Flex align="center" gap={2} py={4}>
                <Spinner size="sm" color="orange.400" />
                <Text fontSize="sm" color="gray.600">
                  Finding available times…
                </Text>
              </Flex>
            )}

            {canProceed && optionsStatus === "error" && (
              <Alert status="warning" borderRadius="10px" fontSize="sm" alignItems="flex-start">
                <AlertIcon />
                <Box>
                  <Text>{optionsError?.message}</Text>
                  {optionsError?.clinicPhone && (
                    <Link href={phoneHref(optionsError.clinicPhone)} display="inline-block" mt={1} fontWeight="600" color="blue.600">
                      {formatPhone(optionsError.clinicPhone)}
                    </Link>
                  )}
                </Box>
              </Alert>
            )}

            {canProceed && optionsStatus === "ready" && (noticeLine || dayEntries.length > 0) && (
              <Text fontSize="sm" color="gray.600">
                {[
                  dayEntries.length > 0
                    ? twoClocks
                      ? `Times are in ${zoneLabel(timeZone)}, the clinic's; ${displayName}'s own time is underneath.`
                      : `Times are in ${zoneLabel(timeZone)}, the clinic's.`
                    : null,
                  noticeLine,
                ]
                  .filter(Boolean)
                  .join(" ")}
              </Text>
            )}

            {canProceed && optionsStatus === "ready" && emptyCopy && (
              <Alert status="info" borderRadius="10px" fontSize="sm" alignItems="flex-start">
                <AlertIcon />
                <Box>
                  <Text fontWeight="600">{emptyCopy.title}</Text>
                  <Text>
                    {windowEnd
                      ? `Nothing is open before ${dayOn(windowEnd, timeZone)}. Look at later dates, or contact the clinic.`
                      : "Please contact the clinic."}
                  </Text>
                </Box>
              </Alert>
            )}

            {canProceed &&
              optionsStatus === "ready" &&
              dayEntries.map(([isoDay, day]) => (
                <Box key={isoDay}>
                  <Text
                    fontSize="11px"
                    letterSpacing="0.04em"
                    textTransform="uppercase"
                    color="gray.500"
                    mb={2}
                  >
                    {dayLabel(isoDay)}
                  </Text>
                  <SimpleGrid minChildWidth="92px" spacing={2}>
                    {day.slots.map((slot) => {
                      const selected = chosen?.start === slot.start;
                      return (
                        <Button
                          key={`${slot.start}-${slot.did || ""}`}
                          size="sm"
                          height="auto"
                          minH="36px"
                          width="100%"
                          py={twoClocks ? 1.5 : 0}
                          flexDirection="column"
                          variant={selected ? "solid" : "outline"}
                          colorScheme={selected ? "orange" : "gray"}
                          aria-pressed={selected}
                          aria-label={
                            twoClocks
                              ? `${timeOn(slot.start, timeZone)} ${zoneLabel(timeZone)}, ${timeOn(slot.start, viewerZone)} your time`
                              : timeOn(slot.start, timeZone)
                          }
                          onClick={() => setChosen({...slot, dayKey: isoDay})}
                          sx={{fontVariantNumeric: "tabular-nums"}}
                        >
                          <span>{timeLabel(slot.start, timeZone)}</span>
                          {twoClocks && (
                            <Text as="span" fontSize="10px" fontWeight="normal" opacity={0.75}>
                              {timeOn(slot.start, viewerZone)}
                            </Text>
                          )}
                        </Button>
                      );
                    })}
                  </SimpleGrid>
                </Box>
              ))}

            {showLater}
          </Stack>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={onClose} isDisabled={submitting}>
            Leave it as it is
          </Button>
          <Button
            colorScheme="orange"
            onClick={submit}
            isLoading={submitting}
            isDisabled={!chosen}
          >
            {chosen ? `Move to ${timeLabel(chosen.start, timeZone)}` : "Pick a time"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Times are the CLINIC's, not the browser's
// ---------------------------------------------------------------------------
//
// Every time on this screen is drawn in the location's own time zone, which the
// availability answer carries in `meta.timeZone`. A guardian travelling, or one
// who simply lives in another province from the clinic, would otherwise be
// offered "9:00" under Tuesday's heading and arrive at 11:00 on the clinic's
// clock — the day comes from the server and the time from the browser, and the
// two disagree by exactly the offset.
//
// The day heading is built from the KEY STRING for the same reason and one
// more: `new Date("2026-09-30")` is parsed as UTC midnight and lands on the
// 29th for everybody west of Greenwich.
function dayLabel(isoDay) {
  const [year, month, day] = String(isoDay || "").split("-").map(Number);
  if (!year || !month || !day) return isoDay || "";
  return new Date(year, month - 1, day).toLocaleDateString(APP_LOCALE, {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
}

function timeLabel(ms, timeZone) {
  if (!ms) return "";
  return new Date(Number(ms)).toLocaleTimeString(APP_LOCALE, {
    hour: "numeric",
    minute: "2-digit",
    ...(timeZone ? {timeZone} : {}),
  });
}
